import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../services/authService';
import { stripBrackets } from '../utils/dateFormat';

const unwrap = (r) => {
  const b = r?.data;
  if (Array.isArray(b)) return b;
  return b?.data ?? [];
};

const ProjectFilterSelect = ({
  value,
  onChange,
  projects = [],
  allLabel = 'All Projects',
  requireSelection = false,
  placeholderLabel = 'Select project',
}) => {
  const [apiProjects, setApiProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadProjects = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await adminAPI.getProjects();
        const rows = unwrap(response).map((row) => ({
          id: row.id,
          name: row.name,
        }));
        if (!cancelled) setApiProjects(rows);
      } catch (error) {
        if (!cancelled) {
          setApiProjects([]);
          setLoadError(error?.response?.data?.message || error?.message || 'Unable to load projects');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadProjects();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const projectOptions = useMemo(() => {
    // Merge prop list (e.g. useProjects) with API — never discard props when API is empty/fails.
    const uniqueById = new Map();
    [...(projects || []), ...apiProjects].forEach((project) => {
      if (project?.id && !uniqueById.has(String(project.id))) {
        uniqueById.set(String(project.id), project);
      }
    });
    const list = Array.from(uniqueById.values());
    list.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return list;
  }, [apiProjects, projects]);

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading && projectOptions.length === 0}
        aria-label={requireSelection ? placeholderLabel : allLabel}
      >
        {requireSelection ? (
          <option value="">{loading ? 'Loading projects…' : placeholderLabel}</option>
        ) : (
          <option value="all">{allLabel}</option>
        )}
        {projectOptions.map((project) => (
          <option key={project.id} value={project.id}>
            {stripBrackets(project.name || '')}
          </option>
        ))}
      </select>
      {!loading && projectOptions.length === 0 ? (
        <span style={{ fontSize: 11, color: '#b91c1c' }}>
          {loadError || 'No projects available.'}
          {' '}
          <button
            type="button"
            className="btn-secondary btn-sm"
            style={{ padding: '0 6px', fontSize: 11 }}
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Retry
          </button>
        </span>
      ) : null}
    </div>
  );
};

export default ProjectFilterSelect;
